import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { distributionCharts, surveyStats } from 'redux/actions/analytics';
import { getCourseAliases } from 'redux/selectors/course';

import useIsMounted from 'shared/hooks/use-is-mounted';

import QuantilesDistribution from './quantiles-distribution';
import { AnalyticsContext } from '../analytics-dashboard';
import AverageChange from './average-change';
import AnalyticsLoader from '../analytics-loader';
import SurveyCharts from './survey-charts';
import PrePostNpsCsatPanel from './pre-post-nps-csat-panel';

const PrePostSurveyReports: React.FC = () => {
  const {
    institutionId, catalogId, analyticsPageType,
    prePostSurveyConfigurationId: configurationId,
  } = useContext(AnalyticsContext);

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useIsMounted();

  const surveyStatsData = useSelector(state => state.app.analytics.surveyStats?.[configurationId] ?? {});
  const aliases = useSelector(state => getCourseAliases(state));

  useEffect(() => {
    const fetchReportsData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          dispatch(
            distributionCharts({ institutionId, preCourseCatalogId: catalogId, configurationId }),
          ),
          dispatch(
            surveyStats({ institutionId, preCourseCatalogId: catalogId, configurationId }),
          ),
        ]);
      } finally {
        if (isMounted()) {
          setIsLoading(false);
        }
      }
    };
    if (configurationId && catalogId) {
      fetchReportsData();
    }
  }, [catalogId, configurationId, dispatch, institutionId]);

  if (isLoading) {
    return (
      <div className='mx-auto reports-container'>
        <AnalyticsLoader />
      </div>
    );
  }

  return (
    <div className='d-flex flex-column align-items-center reports-container mx-auto pb-7'>
      <div className='text-regular-large font-weight-bolder mt-2'>
        {t.COURSE_ANALYTICS.PRE_POST.SUMMARY[analyticsPageType.toUpperCase()]({
          respondersCount: surveyStatsData?.totalResponders,
          totalCount: surveyStatsData?.totalLearners,
          respondersPercentage: surveyStatsData?.engagementPercentage,
          courseCount: surveyStatsData?.totalCourses,
          ...aliases.courseAliases,
          ...aliases.learnersAliases,
        })}
      </div>
      <div className='w-100'>
        <PrePostNpsCsatPanel />
      </div>
      <AverageChange />
      <QuantilesDistribution />
      <SurveyCharts />
    </div>
  );
};

export default PrePostSurveyReports;
